import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  Building,
  Users,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  List,
  CheckSquare,
  Globe,
  Mail,
  Phone,
  AlertCircle,
  Info
} from 'lucide-react';
import api from '../utils/api';

const getAuthToken = () => localStorage.getItem('access_token');

const VariablesSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('custom-values');
  const [loading, setLoading] = useState(false);
  
  // Predefined variables that are always available
  const predefinedTenantVariables = [
    { name: 'Company Name', variable_name: 'company_name', description: 'Your organization name' },
    { name: 'Address', variable_name: 'address', description: 'Your business address' },
    { name: 'Phone', variable_name: 'phone', description: 'Your main phone number' },
    { name: 'Email', variable_name: 'email', description: 'Your main email address' }
  ];

  const predefinedClientFields = [
    { name: 'Client Name', variable_name: 'client_name', label: 'Client Name', type: 'text', description: 'Name of the client' },
    { name: 'Website', variable_name: 'website', label: 'Website', type: 'url', description: 'Client website URL' },
    { name: 'Email', variable_name: 'email', label: 'Email', type: 'email', description: 'Client email address' },
    { name: 'Phone', variable_name: 'phone', label: 'Phone', type: 'phone', description: 'Client phone number' }
  ];

  const predefinedContactFields = [
    { name: 'Contact Name', variable_name: 'contact_name', label: 'Contact Name', type: 'text', description: 'Name of the contact person' },
    { name: 'Email', variable_name: 'email', label: 'Email', type: 'email', description: 'Contact email address' },
    { name: 'Phone', variable_name: 'phone', label: 'Phone', type: 'phone', description: 'Contact phone number' },
    { name: 'Position', variable_name: 'position', label: 'Position', type: 'text', description: 'Job title or position' }
  ];
  
  // Custom Values (Tenant-level)
  const [customValues, setCustomValues] = useState([]);
  const [editingValue, setEditingValue] = useState(null);
  const [newValue, setNewValue] = useState({ name: '', value: '', description: '' });
  
  // Custom Field Definitions (Client-level field types)
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [editingFieldDef, setEditingFieldDef] = useState(null);
  const [newFieldDef, setNewFieldDef] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    options: [],
    description: ''
  });

  // Contact Custom Field Definitions
  const [contactFieldDefs, setContactFieldDefs] = useState([]);
  const [editingContactFieldDef, setEditingContactFieldDef] = useState(null);
  const [newContactFieldDef, setNewContactFieldDef] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    options: [],
    description: ''
  });
  
  const [errors, setErrors] = useState({});

  // Reference Tables
  const [referenceTables, setReferenceTables] = useState([]);
  const [newReferenceTable, setNewReferenceTable] = useState({
    name: '',
    table_type: 'generic',
    description: '',
    custom_columns: []
  });

  const tableTypePresets = {
    generic: {
      description: 'General-purpose list for reusable selections.',
      columns: [
        { key: 'category', label: 'Category', type: 'text' },
        { key: 'notes', label: 'Notes', type: 'text' }
      ]
    },
    pages: {
      description: 'Website pages that can be referenced in portal requests.',
      columns: [
        { key: 'page_type', label: 'Page Type', type: 'text' },
        { key: 'last_modified', label: 'Last Modified', type: 'date' }
      ]
    },
    menu_items: {
      description: 'Navigation links/menu items.',
      columns: [
        { key: 'menu_label', label: 'Menu Label', type: 'text' },
        { key: 'menu_order', label: 'Menu Order', type: 'number' }
      ]
    },
    products: {
      description: 'Products/services and key commerce attributes.',
      columns: [
        { key: 'sku', label: 'SKU', type: 'text' },
        { key: 'price', label: 'Price', type: 'number' },
        { key: 'inventory', label: 'Inventory', type: 'number' }
      ]
    },
    blogs: {
      description: 'Blog ideas/topics and publishing info.',
      columns: [
        { key: 'topic', label: 'Topic', type: 'text' },
        { key: 'publish_date', label: 'Publish Date', type: 'date' }
      ]
    },
    keywords: {
      description: 'SEO keyword lists and planning values.',
      columns: [
        { key: 'search_volume', label: 'Search Volume', type: 'number' },
        { key: 'keyword_difficulty', label: 'Keyword Difficulty', type: 'number' }
      ]
    },
    competitors: {
      description: 'Competitor domains/brands for tracking.',
      columns: [
        { key: 'domain', label: 'Domain', type: 'text' },
        { key: 'segment', label: 'Segment', type: 'text' }
      ]
    }
  };

  const fieldTypes = [
    { value: 'text', label: 'Text', icon: Type },
    { value: 'number', label: 'Number', icon: Hash },
    { value: 'date', label: 'Date', icon: Calendar },
    { value: 'boolean', label: 'Yes/No', icon: ToggleLeft },
    { value: 'select', label: 'Dropdown', icon: List },
    { value: 'multiselect', label: 'Multi-Select', icon: CheckSquare },
    { value: 'url', label: 'URL', icon: Globe },
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'phone', label: 'Phone', icon: Phone }
  ];

  useEffect(() => {
    loadCustomValues();
    loadCustomFieldDefinitions();  
    loadContactCustomFieldDefinitions();
    loadReferenceTables();
    initializePredefinedVariables();
  }, []);

  const initializePredefinedVariables = async () => {
    // This function ensures predefined variables are created if they don't exist
    try {
      // Initialize predefined tenant custom values
      const response = await api.get(`/custom-values`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      const existingValues = response.data || [];
      
      // Check and create predefined tenant variables if they don't exist
      for (const predefined of predefinedTenantVariables) {
        const exists = existingValues.find(v => v.variable_name === predefined.variable_name);
        if (!exists) {
          try {
            await api.post(`/custom-values`, {
              name: predefined.name,
              variable_name: predefined.variable_name,
              value: '', // Empty value for user to fill
              description: predefined.description,
              is_predefined: true
            }, {
              headers: { Authorization: `Bearer ${getAuthToken()}` }
            });
          } catch (error) {
            console.log(`Predefined variable ${predefined.name} might already exist or failed to create`);
          }
        }
      }

      // Initialize predefined client field definitions
      const clientFieldsResponse = await api.get(`/custom-field-definitions`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      const existingClientFields = clientFieldsResponse.data || [];
      
      for (const predefined of predefinedClientFields) {
        const exists = existingClientFields.find(f => f.variable_name === predefined.variable_name);
        if (!exists) {
          try {
            await api.post(`/custom-field-definitions`, {
              name: predefined.name,
              label: predefined.label,
              type: predefined.type,
              description: predefined.description,
              required: false,
              options: [],
              is_predefined: true
            }, {
              headers: { Authorization: `Bearer ${getAuthToken()}` }
            });
          } catch (error) {
            console.log(`Predefined client field ${predefined.name} might already exist or failed to create`);
          }
        }
      }

      // Initialize predefined contact field definitions
      const contactFieldsResponse = await api.get(`/contact-custom-field-definitions`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      const existingContactFields = contactFieldsResponse.data || [];
      
      for (const predefined of predefinedContactFields) {
        const exists = existingContactFields.find(f => f.variable_name === predefined.variable_name);
        if (!exists) {
          try {
            await api.post(`/contact-custom-field-definitions`, {
              name: predefined.name,
              label: predefined.label,
              type: predefined.type,
              description: predefined.description,
              required: false,
              options: [],
              is_predefined: true
            }, {
              headers: { Authorization: `Bearer ${getAuthToken()}` }
            });
          } catch (error) {
            console.log(`Predefined contact field ${predefined.name} might already exist or failed to create`);
          }
        }
      }
      
      // Reload all data after initialization
      loadCustomValues();
      loadCustomFieldDefinitions();
      loadContactCustomFieldDefinitions();
    } catch (error) {
      console.error('Error initializing predefined variables:', error);
    }
  };

  const loadCustomValues = async () => {
    try {
      const response = await api.get(`/custom-values`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setCustomValues(response.data || []);
    } catch (error) {
      console.error('Error loading custom values:', error);
      setCustomValues([]);
    }
  };

  const loadCustomFieldDefinitions = async () => {
    try {
      const response = await api.get(`/custom-field-definitions`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setCustomFieldDefs(response.data || []);
    } catch (error) {
      console.error('Error loading custom field definitions:', error);
      setCustomFieldDefs([]);
    }
  };

  const loadContactCustomFieldDefinitions = async () => {
    try {
      const response = await api.get(`/contact-custom-field-definitions`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setContactFieldDefs(response.data || []);
    } catch (error) {
      console.error('Error loading contact custom field definitions:', error);
      setContactFieldDefs([]);
    }
  };

  const getTeamId = () => user?.team_id || user?.teamId || user?.organization_id || user?.organizationId;

  const loadReferenceTables = async () => {
    try {
      const teamId = getTeamId();
      if (!teamId) {
        setReferenceTables([]);
        return;
      }
      const response = await api.get(`/agent/reference-tables/${teamId}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setReferenceTables(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading reference tables:', error);
      setReferenceTables([]);
    }
  };

  const saveReferenceTable = async () => {
    const teamId = getTeamId();
    if (!teamId) return;
    try {
      await api.post(`/agent/reference-tables/${teamId}/manual`, {
        name: newReferenceTable.name,
        table_type: newReferenceTable.table_type || 'generic',
        description: newReferenceTable.description || '',
        custom_columns: (newReferenceTable.custom_columns || []).filter(c => (c.key || '').trim())
      }, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setNewReferenceTable({ name: '', table_type: 'generic', description: '', custom_columns: [] });
      loadReferenceTables();
    } catch (error) {
      console.error('Error saving reference table:', error);
      alert(error?.response?.data?.detail?.message || 'Failed to save reference table');
    }
  };

  const deleteReferenceTable = async (id) => {
    if (!confirm('Delete this reference table?')) return;
    const teamId = getTeamId();
    if (!teamId) return;
    try {
      await api.delete(`/agent/reference-tables/${teamId}/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      loadReferenceTables();
    } catch (error) {
      console.error('Error deleting reference table:', error);
      alert('Failed to delete reference table');
    }
  };

  const refreshReferenceTable = async (id) => {
    const teamId = getTeamId();
    if (!teamId) return;
    try {
      const response = await api.post(`/agent/reference-tables/${teamId}/${id}/refresh`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      if (!response.data?.success) {
        alert(response.data?.error || 'Refresh failed');
      }
      loadReferenceTables();
    } catch (error) {
      console.error('Error refreshing reference table:', error);
      alert('Failed to refresh reference table');
    }
  };

  const applyTableTypePreset = (tableType, overwrite = false) => {
    const preset = tableTypePresets[tableType] || tableTypePresets.generic;
    setNewReferenceTable(prev => {
      const hasColumns = (prev.custom_columns || []).length > 0;
      if (hasColumns && !overwrite) {
        return { ...prev, table_type: tableType };
      }
      return {
        ...prev,
        table_type: tableType,
        description: prev.description || preset.description,
        custom_columns: (preset.columns || []).map(c => ({ ...c }))
      };
    });
  };

  const addReferenceColumn = () => {
    setNewReferenceTable(prev => ({
      ...prev,
      custom_columns: [...(prev.custom_columns || []), { key: '', label: '', type: 'text' }]
    }));
  };

  const updateReferenceColumn = (idx, patch) => {
    setNewReferenceTable(prev => ({
      ...prev,
      custom_columns: (prev.custom_columns || []).map((c, i) => i === idx ? { ...c, ...patch } : c)
    }));
  };

  const removeReferenceColumn = (idx) => {
    setNewReferenceTable(prev => ({
      ...prev,
      custom_columns: (prev.custom_columns || []).filter((_, i) => i !== idx)
    }));
  };

  const saveCustomValue = async (valueData) => {
    try {
      if (editingValue) {
        await api.put(`/custom-values/${editingValue.id}`, valueData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
      } else {
        await api.post(`/custom-values`, valueData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
      }
      
      loadCustomValues();
      setEditingValue(null);
      setNewValue({ name: '', value: '', description: '' });
    } catch (error) {
      console.error('Error saving custom value:', error);
      alert('Error saving custom value. Please try again.');
    }
  };

  const deleteCustomValue = async (id) => {
    if (!confirm('Are you sure you want to delete this custom value?')) return;
    
    try {
      await api.delete(`/custom-values/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      loadCustomValues();
    } catch (error) {
      console.error('Error deleting custom value:', error);
      alert('Error deleting custom value. Please try again.');
    }
  };

  const saveCustomFieldDefinition = async (fieldData) => {
    try {
      if (editingFieldDef) {
        await api.put(`/custom-field-definitions/${editingFieldDef.id}`, fieldData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
      } else {
        await api.post(`/custom-field-definitions`, fieldData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
      }
      
      loadCustomFieldDefinitions();
      setEditingFieldDef(null);
      setNewFieldDef({
        name: '',
        label: '',
        type: 'text',
        required: false,
        options: [],
        description: ''
      });
    } catch (error) {
      console.error('Error saving custom field definition:', error);
      alert('Error saving custom field definition. Please try again.');
    }
  };

  const deleteCustomFieldDefinition = async (id) => {
    if (!confirm('Are you sure you want to delete this custom field definition? This will remove it from all clients.')) return;
    
    try {
      await api.delete(`/custom-field-definitions/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      loadCustomFieldDefinitions();
    } catch (error) {
      console.error('Error deleting custom field definition:', error);
      alert('Error deleting custom field definition. Please try again.');
    }
  };

  const saveContactCustomFieldDefinition = async (fieldData) => {
    try {
      if (editingContactFieldDef) {
        await api.put(`/contact-custom-field-definitions/${editingContactFieldDef.id}`, fieldData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
      } else {
        await api.post(`/contact-custom-field-definitions`, fieldData, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
      }
      
      loadContactCustomFieldDefinitions();
      setEditingContactFieldDef(null);
      setNewContactFieldDef({
        name: '',
        label: '',
        type: 'text',
        required: false,
        options: [],
        description: ''
      });
    } catch (error) {
      console.error('Error saving contact custom field definition:', error);
      alert('Error saving contact custom field definition. Please try again.');
    }
  };

  const deleteContactCustomFieldDefinition = async (id) => {
    if (!confirm('Are you sure you want to delete this contact custom field definition? This will remove it from all contacts.')) return;
    
    try {
      await api.delete(`/contact-custom-field-definitions/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      loadContactCustomFieldDefinitions();
    } catch (error) {
      console.error('Error deleting contact custom field definition:', error);
      alert('Error deleting contact custom field definition. Please try again.');
    }
  };

  const validateCustomValue = (data) => {
    const newErrors = {};
    if (!data.name.trim()) newErrors.name = 'Name is required';
    // Allow empty values for predefined variables, but require values for custom ones
    if (!data.is_predefined && !data.value.trim()) {
      newErrors.value = 'Value is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCustomFieldDef = (data) => {
    const newErrors = {};
    if (!data.name.trim()) newErrors.name = 'Name is required';
    if (!data.label.trim()) newErrors.label = 'Label is required';
    if ((data.type === 'select' || data.type === 'multiselect') && data.options.length === 0) {
      newErrors.options = 'At least one option is required for dropdown/multi-select fields';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateVariableName = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  };

  const addOption = (fieldData, setFieldData) => {
    const newOptions = [...fieldData.options, ''];
    setFieldData({ ...fieldData, options: newOptions });
  };

  const updateOption = (fieldData, setFieldData, index, value) => {
    const newOptions = [...fieldData.options];
    newOptions[index] = value;
    setFieldData({ ...fieldData, options: newOptions });
  };

  const removeOption = (fieldData, setFieldData, index) => {
    const newOptions = fieldData.options.filter((_, i) => i !== index);
    setFieldData({ ...fieldData, options: newOptions });
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Variables & Custom Fields</h2>
        <p className="text-gray-600">Manage custom values for your organization and custom field definitions for clients.</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('custom-values')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'custom-values'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building className="w-4 h-4 inline mr-2" />
            Custom Values (Organization)
          </button>
          <button
            onClick={() => setActiveTab('custom-fields')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'custom-fields'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Building className="w-4 h-4 inline mr-2" />
            Client Fields (Client Definitions)
          </button>
          <button
            onClick={() => setActiveTab('contact-fields')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contact-fields'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Contact Fields (Contact Definitions)
          </button>
          {/* Reference Tables moved to Client Settings > Data Tables */}
        </nav>
      </div>

      {/* Custom Values Tab */}
      {activeTab === 'custom-values' && (
        <div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-900 mb-1">Organization Custom Values</h4>
                <p className="text-sm text-blue-700 mb-2">
                  These values are available across your entire organization in templates using variables like <code className="bg-blue-100 px-1 rounded">{`{{tenant.company_name}}`}</code>
                </p>
                <p className="text-xs text-blue-600">
                  <strong>Predefined variables</strong> are commonly used business values that are automatically created for you. 
                  Simply set their values. <strong>Custom variables</strong> are additional ones you create for your specific needs.
                </p>
              </div>
            </div>
          </div>

          {/* Add/Edit Custom Value Form */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingValue ? 'Edit Custom Value' : 'Add Custom Value'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={editingValue ? editingValue.name : newValue.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingValue) {
                      setEditingValue({ ...editingValue, name: value });
                    } else {
                      setNewValue({ ...newValue, name: value });
                    }
                  }}
                  disabled={editingValue && editingValue.is_predefined}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    editingValue && editingValue.is_predefined ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="e.g., Company Name"
                />
                {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  Variable: <code>{`{{tenant.${generateVariableName(editingValue ? editingValue.name : newValue.name)}}}`}</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Value {editingValue && editingValue.is_predefined ? '' : '*'}</label>
                <input
                  type="text"
                  value={editingValue ? editingValue.value : newValue.value}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingValue) {
                      setEditingValue({ ...editingValue, value: value });
                    } else {
                      setNewValue({ ...newValue, value: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={editingValue && editingValue.is_predefined ? 
                    "Enter your " + editingValue.name.toLowerCase() :
                    "e.g., Acme Corporation"
                  }
                />
                {errors.value && <p className="text-sm text-red-600 mt-1">{errors.value}</p>}
                {editingValue && editingValue.is_predefined && (
                  <p className="text-xs text-blue-600 mt-1">
                    This is a predefined variable. You can customize the value.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={editingValue ? editingValue.description : newValue.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingValue) {
                      setEditingValue({ ...editingValue, description: value });
                    } else {
                      setNewValue({ ...newValue, description: value });
                    }
                  }}
                  disabled={editingValue && editingValue.is_predefined}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    editingValue && editingValue.is_predefined ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="Brief description of this value"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  const data = editingValue || newValue;
                  if (validateCustomValue(data)) {
                    saveCustomValue(data);
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingValue ? 'Update' : 'Add'} Custom Value
              </button>
              
              {editingValue && (
                <button
                  onClick={() => {
                    setEditingValue(null);
                    setErrors({});
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Custom Values List */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Organization Variables</h3>
              <p className="text-sm text-gray-600 mt-1">
                Predefined variables are highlighted and commonly used. Custom variables are additional ones you create.
              </p>
            </div>
            
            {customValues.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Building className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>Loading organization variables...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {/* Sort predefined variables first, then custom ones */}
                {customValues
                  .sort((a, b) => (b.is_predefined ? 1 : 0) - (a.is_predefined ? 1 : 0))
                  .map((value) => (
                  <div key={value.id} className={`p-6 flex items-center justify-between ${
                    value.is_predefined ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-sm font-medium text-gray-900">{value.name}</h4>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {`{{tenant.${value.variable_name || generateVariableName(value.name)}}}`}
                        </code>
                        {value.is_predefined && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Predefined
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {value.value || (
                          <span className="italic text-gray-400">
                            {value.is_predefined ? 'Click edit to set your value' : 'No value set'}
                          </span>
                        )}
                      </p>
                      {value.description && (
                        <p className="text-xs text-gray-500 mt-1">{value.description}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingValue(value)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {!value.is_predefined && (
                        <button
                          onClick={() => deleteCustomValue(value.id)}
                          className="p-2 text-red-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Field Definitions Tab */}
      {activeTab === 'custom-fields' && (
        <div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-orange-900 mb-1">Client Custom Field Definitions</h4>
                <p className="text-sm text-orange-700 mb-2">
                  Define what custom fields are available for clients. Values are set per-client and used in templates like <code className="bg-orange-100 px-1 rounded">{`{{client.industry}}`}</code>
                </p>
                <p className="text-xs text-orange-600">
                  <strong>Predefined fields</strong> are commonly used client properties that are automatically created for you. 
                  <strong>Custom fields</strong> are additional ones you create for your specific needs.
                </p>
              </div>
            </div>
          </div>

          {/* Add/Edit Custom Field Definition Form */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingFieldDef ? 'Edit Custom Field Definition' : 'Add Custom Field Definition'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
                <input
                  type="text"
                  value={editingFieldDef ? editingFieldDef.name : newFieldDef.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingFieldDef) {
                      setEditingFieldDef({ ...editingFieldDef, name: value });
                    } else {
                      setNewFieldDef({ ...newFieldDef, name: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Industry"
                />
                {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  Variable: <code>{`{{client.${generateVariableName(editingFieldDef ? editingFieldDef.name : newFieldDef.name)}}}`}</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Label *</label>
                <input
                  type="text"
                  value={editingFieldDef ? editingFieldDef.label : newFieldDef.label}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingFieldDef) {
                      setEditingFieldDef({ ...editingFieldDef, label: value });
                    } else {
                      setNewFieldDef({ ...newFieldDef, label: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Industry Type"
                />
                {errors.label && <p className="text-sm text-red-600 mt-1">{errors.label}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Type *</label>
                <select
                  value={editingFieldDef ? editingFieldDef.type : newFieldDef.type}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingFieldDef) {
                      setEditingFieldDef({ ...editingFieldDef, type: value, options: [] });
                    } else {
                      setNewFieldDef({ ...newFieldDef, type: value, options: [] });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {fieldTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={editingFieldDef ? editingFieldDef.description : newFieldDef.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingFieldDef) {
                      setEditingFieldDef({ ...editingFieldDef, description: value });
                    } else {
                      setNewFieldDef({ ...newFieldDef, description: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Brief description of this field"
                />
              </div>
            </div>

            {/* Options for select/multiselect */}
            {((editingFieldDef && (editingFieldDef.type === 'select' || editingFieldDef.type === 'multiselect')) || 
              (newFieldDef.type === 'select' || newFieldDef.type === 'multiselect')) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Options *</label>
                <div className="space-y-2">
                  {(editingFieldDef ? editingFieldDef.options : newFieldDef.options).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          if (editingFieldDef) {
                            updateOption(editingFieldDef, setEditingFieldDef, index, e.target.value);
                          } else {
                            updateOption(newFieldDef, setNewFieldDef, index, e.target.value);
                          }
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={`Option ${index + 1}`}
                      />
                      <button
                        onClick={() => {
                          if (editingFieldDef) {
                            removeOption(editingFieldDef, setEditingFieldDef, index);
                          } else {
                            removeOption(newFieldDef, setNewFieldDef, index);
                          }
                        }}
                        className="p-2 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      if (editingFieldDef) {
                        addOption(editingFieldDef, setEditingFieldDef);
                      } else {
                        addOption(newFieldDef, setNewFieldDef);
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Option
                  </button>
                </div>
                {errors.options && <p className="text-sm text-red-600 mt-1">{errors.options}</p>}
              </div>
            )}

            {/* Required checkbox */}
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingFieldDef ? editingFieldDef.required : newFieldDef.required}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (editingFieldDef) {
                      setEditingFieldDef({ ...editingFieldDef, required: checked });
                    } else {
                      setNewFieldDef({ ...newFieldDef, required: checked });
                    }
                  }}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Required Field</span>
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  const data = editingFieldDef || newFieldDef;
                  if (validateCustomFieldDef(data)) {
                    saveCustomFieldDefinition(data);
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingFieldDef ? 'Update' : 'Add'} Field Definition
              </button>
              
              {editingFieldDef && (
                <button
                  onClick={() => {
                    setEditingFieldDef(null);
                    setErrors({});
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Custom Field Definitions List */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Client Field Definitions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Predefined fields are commonly used client properties. Custom fields are additional ones you create.
              </p>
            </div>
            
            {customFieldDefs.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>Loading client field definitions...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {customFieldDefs
                  .sort((a, b) => (b.is_predefined ? 1 : 0) - (a.is_predefined ? 1 : 0))
                  .map((fieldDef) => {
                  const fieldTypeInfo = fieldTypes.find(ft => ft.value === fieldDef.type);
                  const FieldIcon = fieldTypeInfo?.icon || Type;
                  
                  return (
                    <div key={fieldDef.id} className={`p-6 flex items-start justify-between ${
                      fieldDef.is_predefined ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
                    }`}>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <FieldIcon className="w-4 h-4 text-gray-400" />
                          <h4 className="text-sm font-medium text-gray-900">{fieldDef.label}</h4>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {`{{client.${fieldDef.variable_name || generateVariableName(fieldDef.name)}}}`}
                          </code>
                          {fieldDef.is_predefined && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              Predefined
                            </span>
                          )}
                          {fieldDef.required && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>Type: {fieldTypeInfo?.label}</span>
                          {fieldDef.options && fieldDef.options.length > 0 && (
                            <span>Options: {fieldDef.options.join(', ')}</span>
                          )}
                        </div>
                        
                        {fieldDef.description && (
                          <p className="text-xs text-gray-500 mt-1">{fieldDef.description}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingFieldDef(fieldDef)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {!fieldDef.is_predefined && (
                          <button
                            onClick={() => deleteCustomFieldDefinition(fieldDef.id)}
                            className="p-2 text-red-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Field Definitions Tab */}
      {activeTab === 'contact-fields' && (
        <div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-green-900 mb-1">Contact Custom Field Definitions</h4>
                <p className="text-sm text-green-700 mb-2">
                  Define what custom fields are available for individual contacts. Values are set per-contact and used in templates like <code className="bg-green-100 px-1 rounded">{`{{contact.role}}`}</code>
                </p>
                <p className="text-xs text-green-600">
                  <strong>Predefined fields</strong> are commonly used contact properties that are automatically created for you. 
                  <strong>Custom fields</strong> are additional ones you create for your specific needs.
                </p>
              </div>
            </div>
          </div>

          {/* Add/Edit Contact Custom Field Definition Form */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingContactFieldDef ? 'Edit Contact Field Definition' : 'Add Contact Field Definition'}  
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
                <input
                  type="text"
                  value={editingContactFieldDef ? editingContactFieldDef.name : newContactFieldDef.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingContactFieldDef) {
                      setEditingContactFieldDef({ ...editingContactFieldDef, name: value });
                    } else {
                      setNewContactFieldDef({ ...newContactFieldDef, name: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Role"
                />
                {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  Variable: <code>{`{{contact.${generateVariableName(editingContactFieldDef ? editingContactFieldDef.name : newContactFieldDef.name)}}}`}</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Display Label *</label>
                <input
                  type="text"
                  value={editingContactFieldDef ? editingContactFieldDef.label : newContactFieldDef.label}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingContactFieldDef) {
                      setEditingContactFieldDef({ ...editingContactFieldDef, label: value });
                    } else {
                      setNewContactFieldDef({ ...newContactFieldDef, label: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Job Role"
                />
                {errors.label && <p className="text-sm text-red-600 mt-1">{errors.label}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Type *</label>
                <select
                  value={editingContactFieldDef ? editingContactFieldDef.type : newContactFieldDef.type}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingContactFieldDef) {
                      setEditingContactFieldDef({ ...editingContactFieldDef, type: value, options: [] });
                    } else {
                      setNewContactFieldDef({ ...newContactFieldDef, type: value, options: [] });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {fieldTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <input
                  type="text"
                  value={editingContactFieldDef ? editingContactFieldDef.description : newContactFieldDef.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (editingContactFieldDef) {
                      setEditingContactFieldDef({ ...editingContactFieldDef, description: value });
                    } else {
                      setNewContactFieldDef({ ...newContactFieldDef, description: value });
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Brief description of this field"
                />
              </div>
            </div>

            {/* Options for select/multiselect */}
            {((editingContactFieldDef && (editingContactFieldDef.type === 'select' || editingContactFieldDef.type === 'multiselect')) || 
              (newContactFieldDef.type === 'select' || newContactFieldDef.type === 'multiselect')) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Options *</label>
                <div className="space-y-2">
                  {(editingContactFieldDef ? editingContactFieldDef.options : newContactFieldDef.options).map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          if (editingContactFieldDef) {
                            updateOption(editingContactFieldDef, setEditingContactFieldDef, index, e.target.value);
                          } else {
                            updateOption(newContactFieldDef, setNewContactFieldDef, index, e.target.value);
                          }
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder={`Option ${index + 1}`}
                      />
                      <button
                        onClick={() => {
                          if (editingContactFieldDef) {
                            removeOption(editingContactFieldDef, setEditingContactFieldDef, index);
                          } else {
                            removeOption(newContactFieldDef, setNewContactFieldDef, index);
                          }
                        }}
                        className="p-2 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      if (editingContactFieldDef) {
                        addOption(editingContactFieldDef, setEditingContactFieldDef);
                      } else {
                        addOption(newContactFieldDef, setNewContactFieldDef);
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Option
                  </button>
                </div>
                {errors.options && <p className="text-sm text-red-600 mt-1">{errors.options}</p>}
              </div>
            )}

            {/* Required checkbox */}
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingContactFieldDef ? editingContactFieldDef.required : newContactFieldDef.required}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (editingContactFieldDef) {
                      setEditingContactFieldDef({ ...editingContactFieldDef, required: checked });
                    } else {
                      setNewContactFieldDef({ ...newContactFieldDef, required: checked });
                    }
                  }}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Required Field</span>
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  const data = editingContactFieldDef || newContactFieldDef;
                  if (validateCustomFieldDef(data)) {
                    saveContactCustomFieldDefinition(data);
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingContactFieldDef ? 'Update' : 'Add'} Contact Field Definition
              </button>
              
              {editingContactFieldDef && (
                <button
                  onClick={() => {
                    setEditingContactFieldDef(null);
                    setErrors({});
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Contact Custom Field Definitions List */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Contact Field Definitions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Predefined fields are commonly used contact properties. Custom fields are additional ones you create.
              </p>
            </div>
            
            {contactFieldDefs.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>Loading contact field definitions...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {contactFieldDefs
                  .sort((a, b) => (b.is_predefined ? 1 : 0) - (a.is_predefined ? 1 : 0))
                  .map((fieldDef) => {
                  const fieldTypeInfo = fieldTypes.find(ft => ft.value === fieldDef.type);
                  const FieldIcon = fieldTypeInfo?.icon || Type;
                  
                  return (
                    <div key={fieldDef.id} className={`p-6 flex items-start justify-between ${
                      fieldDef.is_predefined ? 'bg-green-50 border-l-4 border-l-green-500' : ''
                    }`}>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <FieldIcon className="w-4 h-4 text-gray-400" />
                          <h4 className="text-sm font-medium text-gray-900">{fieldDef.label}</h4>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {`{{contact.${fieldDef.variable_name || generateVariableName(fieldDef.name)}}}`}
                          </code>
                          {fieldDef.is_predefined && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Predefined
                            </span>
                          )}
                          {fieldDef.required && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Required
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>Type: {fieldTypeInfo?.label}</span>
                          {fieldDef.options && fieldDef.options.length > 0 && (
                            <span>Options: {fieldDef.options.join(', ')}</span>
                          )}
                        </div>
                        
                        {fieldDef.description && (
                          <p className="text-xs text-gray-500 mt-1">{fieldDef.description}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingContactFieldDef(fieldDef)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {!fieldDef.is_predefined && (
                          <button
                            onClick={() => deleteContactCustomFieldDefinition(fieldDef.id)}
                            className="p-2 text-red-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reference Tables moved to Client Settings > Data Tables */}
    </div>
  );
};

export default VariablesSettings;
